import { edgeColumns } from "./edgeColumns.ts";

interface SupabaseTransformBuilderLike {
  order(column: string, options: { ascending: boolean }): SupabaseTransformBuilderLike;
  limit(count: number): SupabaseTransformBuilderLike;
  maybeSingle(): PromiseLike<{ data: unknown; error: { message?: string } | null }>;
}

interface SupabaseQueryBuilderLike extends SupabaseTransformBuilderLike {
  eq(column: string, value: unknown): SupabaseQueryBuilderLike;
}

interface SupabaseTableLike {
  select(columns: string): SupabaseQueryBuilderLike;
}

export interface SupabaseLikeClient {
  // The SDK returns a generic table builder; narrow its used operations below.
  from(table: string): unknown;
}

interface FrameworkPackSnapshot {
  id: string;
  label: string;
  family: string;
  source: string;
  preview: {
    philosophySummary: string;
    whatItNotices: string[];
    watchouts: string[];
    sampleCoachLanguage: string[];
    sampleLeaderLanguage: string[];
  };
  runtime: {
    coreConstructs: string[];
    diagnosticQuestions: string[];
    interventionPatterns: string[];
  };
}

export interface CoachLens {
  lens_name: string | null;
  reflection_style: string | null;
  preferred_language: string[] | null;
  avoid_language: string[] | null;
  question_stems: string[] | null;
  frameworks: { name: string; summary: string }[] | null;
  coach_lens_json?: {
    philosophy?: string;
    frameworks_raw?: string;
    frameworks_normalized?: string;
    framework_pack_id?: string | null;
    framework_pack?: FrameworkPackSnapshot | null;
    identity_report?: {
      status?: "empty" | "parsed" | "partial" | "failed";
      parsedSectionCount?: number;
      referenceCount?: number;
      parsedReferenceCount?: number;
    };
    tone_preset?: string;
    avoid?: string[];
    boundaries?: { no_behavior_changes_without_coach?: boolean };
  } | null;
  normalization_version: number | null;
}

export interface CoachTwin {
  version: number;
  voice_summary?: string;
  question_stems?: string[];
  phrase_bank?: string[];
  avoid_list?: string[];
  coaching_moves?: string[];
  guardrails?: string[];
  identity_template_preview?: {
    lensLabel?: string;
    philosophySummary?: string;
    voiceSummary?: string;
    frameworkPackId?: string | null;
    frameworkPackLabel?: string | null;
    voiceCustomizationStatus?: "customized" | "framework_default" | "default" | null;
    referenceCount?: number;
    parsedReferenceCount?: number;
  } | null;
}

export interface CoachInfluenceVersions {
  loaderVersion: "coach-influence-context.v1";
  lensNormalizationVersion: number | null;
  twinVersion: number | null;
}

export interface CoachInfluenceContext {
  coachLens: CoachLens | null;
  coachTwin: CoachTwin | null;
  versions: CoachInfluenceVersions;
}

interface CoachLensRow {
  lens_name: string | null;
  reflection_style: string | null;
  preferred_language: string[] | null;
  avoid_language: string[] | null;
  question_stems: string[] | null;
  frameworks: { name: string; summary: string }[] | null;
  coach_lens_json?: {
    philosophy?: string;
    frameworks_raw?: string;
    frameworks_normalized?: string;
    framework_pack_id?: string | null;
    framework_pack?: FrameworkPackSnapshot | null;
    identity_report?: {
      status?: "empty" | "parsed" | "partial" | "failed";
      parsedSectionCount?: number;
      referenceCount?: number;
      parsedReferenceCount?: number;
    };
    tone_preset?: string;
    avoid?: string[];
    boundaries?: { no_behavior_changes_without_coach?: boolean };
  } | null;
  normalization_version: number | null;
}

interface CoachTwinRow {
  version: number;
  twin_json: Record<string, unknown> | null;
}

interface CoachProfileRow {
  coaching_philosophy: string | null;
  frameworks: string | null;
  language_preferences: string | null;
  never_do: string | null;
}

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter((v): v is string => typeof v === "string");
  return out.length > 0 ? out : undefined;
}

export async function loadCoachInfluenceContext(
  supabase: SupabaseLikeClient,
  coachId: string,
  options: { throwOnError?: boolean } = {},
): Promise<CoachInfluenceContext> {
  const [lensResult, twinResult, coachProfileResult] = await Promise.all([
    (supabase.from("coach_lens") as SupabaseTableLike)
      .select(edgeColumns("coach_lens", ["lens_name", "reflection_style", "preferred_language", "avoid_language", "question_stems", "frameworks", "coach_lens_json", "normalization_version"]))
      .eq("coach_id", coachId)
      .eq("is_active", true)
      .maybeSingle(),
    (supabase.from("coach_twin_profiles") as SupabaseTableLike)
      .select(edgeColumns("coach_twin_profiles", ["version", "twin_json"]))
      .eq("coach_id", coachId)
      .eq("status", "active")
      .order("version", { ascending: false })
      .limit(1)
      .maybeSingle(),
    (supabase.from("coach_profiles") as SupabaseTableLike)
      .select(edgeColumns("coach_profiles", ["coaching_philosophy", "frameworks", "language_preferences", "never_do"]))
      .eq("coach_id", coachId)
      .maybeSingle(),
  ]);

  if (lensResult.error) {
    console.warn("[coachInfluenceContext] coach_lens query failed:", lensResult.error.message);
  }
  if (twinResult.error) {
    console.warn("[coachInfluenceContext] coach_twin_profiles query failed:", twinResult.error.message);
  }
  if (coachProfileResult.error) {
    console.warn("[coachInfluenceContext] coach_profiles query failed:", coachProfileResult.error.message);
  }

  if (options.throwOnError && (lensResult.error || twinResult.error || coachProfileResult.error)) {
    throw new Error("coach_identity_query_failed");
  }

  const lensRow = lensResult.data as CoachLensRow | null;
  const twinRow = twinResult.data as CoachTwinRow | null;
  const coachProfileRow = coachProfileResult.data as CoachProfileRow | null;

  const coachLens: CoachLens | null = lensRow
    ? {
        lens_name: lensRow.lens_name,
        reflection_style: lensRow.reflection_style,
        preferred_language: lensRow.preferred_language?.length
          ? lensRow.preferred_language
          : coachProfileRow?.language_preferences
            ? [coachProfileRow.language_preferences]
            : [],
        avoid_language: lensRow.avoid_language?.length
          ? lensRow.avoid_language
          : coachProfileRow?.never_do
            ? [coachProfileRow.never_do]
            : [],
        question_stems: lensRow.question_stems,
        frameworks: lensRow.frameworks,
        coach_lens_json: {
          ...(lensRow.coach_lens_json || {}),
          philosophy: lensRow.coach_lens_json?.philosophy || coachProfileRow?.coaching_philosophy || undefined,
          frameworks_raw: lensRow.coach_lens_json?.frameworks_raw || coachProfileRow?.frameworks || undefined,
        },
        normalization_version: lensRow.normalization_version ?? null,
      }
    : coachProfileRow?.coaching_philosophy || coachProfileRow?.frameworks
      ? {
          lens_name: null,
          reflection_style: null,
          preferred_language: coachProfileRow?.language_preferences
            ? [coachProfileRow.language_preferences]
            : [],
          avoid_language: coachProfileRow?.never_do
            ? [coachProfileRow.never_do]
            : [],
          question_stems: [],
          frameworks: [],
          coach_lens_json: {
            philosophy: coachProfileRow?.coaching_philosophy || undefined,
            frameworks_raw: coachProfileRow?.frameworks || undefined,
          },
          normalization_version: null,
        }
    : null;

  let coachTwin: CoachTwin | null = null;
  if (twinRow?.twin_json) {
    const twinJson = twinRow.twin_json;
    coachTwin = {
      version: twinRow.version,
      voice_summary: typeof twinJson.voice_summary === "string" ? twinJson.voice_summary : undefined,
      question_stems: asStringArray(twinJson.question_stems),
      phrase_bank: asStringArray(twinJson.phrase_bank),
      avoid_list: asStringArray(twinJson.avoid_list),
      coaching_moves: asStringArray(twinJson.coaching_moves),
      guardrails: asStringArray(twinJson.guardrails),
      identity_template_preview: twinJson.identity_template_preview && typeof twinJson.identity_template_preview === "object"
        ? twinJson.identity_template_preview as CoachTwin["identity_template_preview"]
        : null,
    };
  }

  return {
    coachLens,
    coachTwin,
    versions: {
      loaderVersion: "coach-influence-context.v1",
      lensNormalizationVersion: coachLens?.normalization_version ?? null,
      twinVersion: coachTwin?.version ?? null,
    },
  };
}
